import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Divider,
    FormControlLabel,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Checkbox,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    TableContainer
} from "@mui/material";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useDispatch, useSelector } from "react-redux";
import Label from "../../../components/Label/Label";
import { clearFilteredBatchList } from "../../../redux/visualization/actions";
import { fetchDashboardFilteredBatchList, setSelectedBatchIds } from "../../../redux/dashboard/actions";
import Buttons from "../../../components/Controls/Buttons/Button";
import { formatLocal } from "../../../utility";

const DashboardBatchPanel = ({
    setPreviewData,
    setShowPreview,
    onClose,
    productId,
    siteId,
    batchState,
    updateState,
    onClickApply,
    disableAllFields,
    kpitableres
}) => {
    const dispatch = useDispatch();

    const selectedBatchIds = useSelector(
        (state) => state.dashboard.selectedBatchIds || []
    );

    const [loader, setLoader] = useState(false);
    const [localData, setLocalData] = useState([]);
    const [search, setSearch] = useState("");
    const [previewClicked, setPreviewClicked] = useState(false);

    /* ---------------- RESET ---------------- */
    useEffect(() => {
        dispatch(clearFilteredBatchList());
        setLocalData([]);
        setPreviewClicked(false);
    }, [productId, siteId]);

    /* ---------------- PREVIEW ---------------- */
    const handlePreview = async () => {
        setLoader(true);
        setSearch("");
        setPreviewClicked(true);

        const payload = {
            main_product_name: productId,
            site_variant_id: siteId,
            batchs_selection: {
                batch_range_option: batchState.batchRangeOption,
                recent_batch_count: batchState.recentBatchCount,
                recent_day_count: batchState.recentDayCount,
                start_date: formatLocal(batchState.startDate),
                end_date: formatLocal(batchState.endDate),
            },
        };

        try {
            const res = await dispatch(fetchDashboardFilteredBatchList(payload));

            const formatted = (res || []).map((b) => ({
                ...b,
                batch_id: String(b.batch_id),
            }));

            setLocalData(formatted);

            const allIds = formatted.map((b) => String(b.batch_id));

            // ✅ MAIN FIX: if nothing selected → select all
            if (!selectedBatchIds || selectedBatchIds.length === 0) {
                dispatch(setSelectedBatchIds(allIds));
            }

        } catch (error) {
            console.error("Error fetching batch preview:", error);
            setLocalData([]);
        } finally {
            setLoader(false);
        }
    };

    /* ---------------- SYNC SELECTION WITH DATA ---------------- */
    useEffect(() => {
        if (localData.length === 0) return;

        const validIds = localData.map((b) => String(b.batch_id));

        // keep only IDs that exist in current preview
        const filteredSelected = selectedBatchIds.filter((id) =>
            validIds.includes(id)
        );

        // update redux only if mismatch
        if (filteredSelected.length !== selectedBatchIds.length) {
            dispatch(setSelectedBatchIds(filteredSelected));
        }
    }, [localData]);

    /* ---------------- FILTER ---------------- */
    const filteredData = useMemo(() => {
        return localData.filter((b) =>
            String(b.batch_id)
                .toLowerCase()
                .includes(search.trim().toLowerCase())
        );
    }, [search, localData]);

    /* ---------------- DERIVED SELECTION ---------------- */
    const dataWithSelection = useMemo(() => {
        return filteredData.map((b) => ({
            ...b,
            isSelected: selectedBatchIds.includes(String(b.batch_id)),
        }));
    }, [filteredData, selectedBatchIds]);

    const isAllSelected =
        dataWithSelection.length > 0 &&
        dataWithSelection.every((b) => b.isSelected);

    const isIndeterminate =
        dataWithSelection.some((b) => b.isSelected) && !isAllSelected;

    /* ---------------- SELECTION HANDLERS ---------------- */
    const toggleSelect = (id) => {
        const normalizedId = String(id);

        const updated = selectedBatchIds.includes(normalizedId)
            ? selectedBatchIds.filter((x) => x !== normalizedId)
            : [...selectedBatchIds, normalizedId];

        dispatch(setSelectedBatchIds(updated));
    };

    const handleSelectAll = (checked) => {
        const idsInView = filteredData.map((b) => String(b.batch_id));

        let updated = [...selectedBatchIds];

        idsInView.forEach((id) => {
            if (checked) {
                if (!updated.includes(id)) updated.push(id);
            } else {
                updated = updated.filter((x) => x !== id);
            }
        });

        dispatch(setSelectedBatchIds(updated));
    };

    const totalSelected = selectedBatchIds.length;

    /* ---------------- VALIDATION ---------------- */
    const isDisabledPreview = () => {
        const {
            batchRangeOption,
            recentBatchCount,
            recentDayCount,
            startDate,
            endDate,
        } = batchState || {};

        if (!batchRangeOption) return true;
        if (!productId || !siteId) return true;
        if (batchRangeOption === "by_latest_x" && !recentBatchCount) return true;
        if (batchRangeOption === "by_recent_x_days" && !recentDayCount) return true;
        if (batchRangeOption === "by_date_x" && (!startDate || !endDate))
            return true;

        return false;
    };

    const handleRadioChange = (e) => {
        const selected = e.target.value;

        updateState("batchRangeOption", selected);

        updateState("recentBatchCount", "");
        updateState("recentDayCount", "");
        updateState("startDate", null);
        updateState("endDate", null);
        dispatch(setSelectedBatchIds([]));
    };

    const handleBatchChange = (key, value) => {
        updateState(key, value);
        setPreviewClicked(false);
        dispatch(setSelectedBatchIds([]));
    }

    return (
        <Box container spacing={2} sx={{ display: 'flex' }}>
            {/* ================= LEFT ================= */}
            <Box item width={'300px'} sx={{ p: 0.5, borderRadius: '8px' }}>
                <Card sx={{ minHeight: 420 }}>
                    <CardContent>
                        <Label text="Batch Range" fontWeight="500" />

                        <RadioGroup
                            value={batchState?.batchRangeOption}
                            onChange={handleRadioChange}
                        >
                            <FormControlLabel
                                value="by_latest_x"
                                control={<Radio size="small" />}
                                label={
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        placeholder="Recent Batches"
                                        disabled={batchState?.batchRangeOption !== "by_latest_x"}
                                        value={batchState?.recentBatchCount}
                                        onChange={(e) =>
                                            handleBatchChange("recentBatchCount", e.target.value)
                                        }
                                    />
                                }
                            />

                            <Divider sx={{ my: 1 }} />

                            <FormControlLabel
                                value="by_recent_x_days"
                                control={<Radio size="small" />}
                                label={
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        autoComplete="off"
                                        placeholder="Recent Days"
                                        disabled={batchState?.batchRangeOption !== "by_recent_x_days"}
                                        value={batchState?.recentDayCount}
                                        onChange={(e) =>
                                            handleBatchChange("recentDayCount", e.target.value)
                                        }
                                    />
                                }
                            />

                            <Divider sx={{ my: 1 }} />

                            <FormControlLabel
                                value="by_date_x"
                                control={<Radio size="small" />}
                                label={
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        {["startDate", "endDate"].map((key, i) => (
                                            <DatePicker
                                                key={key}
                                                selected={batchState?.[key] || null}
                                                onChange={(date) => handleBatchChange(key, date)}
                                                placeholderText={i === 0 ? "From" : "To"}
                                                autoComplete="off"
                                                showYearDropdown
                                                disabled={batchState?.batchRangeOption !== "by_date_x"}
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={150}
                                                customInput={
                                                    <TextField
                                                        size="small"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <CalendarTodayIcon fontSize="small" />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                }
                                            />
                                        ))}
                                    </Box>
                                }
                            />
                        </RadioGroup>

                        <Box mt={2} display={"flex"} gap={1}>
                            <Buttons
                                fullWidth
                                variant="contained"
                                onClick={onClose}
                                text={'Cancel'}
                            />
                            <Buttons
                                fullWidth
                                variant="contained"
                                startIcon={<PreviewOutlinedIcon />}
                                disabled={isDisabledPreview()}
                                onClick={handlePreview}
                                loading={kpitableres?.dashboardBatchData?.loading}
                                text={'Preview'}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* ================= RIGHT ================= */}
            <Box item width={'100%'} sx={{ p: 0.5, borderRadius: '8px' }}>
                {previewClicked && (
                    <Card sx={{ minHeight: 420, overflow: 'auto' }}>
                        <CardContent>
                            <Label
                                text={`Batch Preview (${totalSelected}/${localData.length})`}
                                fontWeight="500"
                            />

                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search Batch ID"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ mb: 0.5 }}
                                autoComplete="off"
                            />

                            {loader ? (
                                <Box textAlign="center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer sx={{ height: 270, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader tableLayout="fixed">
                                        <TableHead >
                                            <TableRow>
                                                <TableCell class='table-bg'>
                                                    <Checkbox
                                                        checked={isAllSelected}
                                                        indeterminate={isIndeterminate}
                                                        // sx={{ p: 0 }}
                                                        onChange={(e) =>
                                                            handleSelectAll(e.target.checked)
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell class='table-bg'>Batch ID</TableCell>
                                                <TableCell class='table-bg'>Date</TableCell>
                                                <TableCell class='table-bg'>Unit Operation</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody
                                            sx={{
                                                "& .MuiTableRow-root": { height: 45 },
                                                "& .MuiTableCell-root": { py: 0.4 },
                                            }}
                                        >
                                            {dataWithSelection.map((b, index) => (
                                                <TableRow key={index}>
                                                    <TableCell sx={{ px: 1  }}>
                                                        <Checkbox
                                                            checked={b.isSelected}
                                                            onChange={() => toggleSelect(b.batch_id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ px: 1  }}>{b.batch_id_non_unique}</TableCell>
                                                    <TableCell sx={{ minWidth: 'max-conent', px: 1 }}>
                                                        {b.batch_date}
                                                    </TableCell>
                                                    <TableCell sx={{ px: 1 }}>{b.unit_op_name}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Box mt={1} display="flex" justifyContent="flex-end">
                                <Buttons
                                    loading={kpitableres?.kpitable?.loading}
                                    disabled={totalSelected === 0}
                                    text={'Apply'}
                                    onClick={() => {
                                        const idsToApply =
                                            selectedBatchIds.length === 0
                                                ? localData.map((b) => String(b.batch_id))
                                                : selectedBatchIds;

                                        onClickApply(idsToApply);
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Box>
    );
};

export default DashboardBatchPanel;